import { useRef, useState, useEffect } from "react";
import { Play, Pause } from "lucide-react";

const BAR_COUNT = 36;

export default function AudioPlayer({ src, title, kicker, testid }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setProgress(el.duration ? el.currentTime / el.duration : 0);
    const onMeta = () => setDuration(el.duration || 0);
    const onEnd = () => setPlaying(false);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("ended", onEnd);

    el.muted = true;
    el.play()
      .then(() => setPlaying(true))
      .catch(() => {});

    // Browsers block sound until the visitor interacts, so turn sound on at
    // the first click/tap/key anywhere on the page (the toggle handles itself).
    // Only click/touchend/keydown count as a gesture on iOS Safari, and we keep
    // listening until playback with sound actually succeeds.
    const events = ["click", "touchend", "keydown"];
    const stop = () => events.forEach((t) => document.removeEventListener(t, unlock, true));
    const unlock = (e) => {
      if (e.target?.closest?.(`[data-testid="${testid}-toggle"]`)) {
        stop();
        return;
      }
      el.muted = false;
      el.play()
        .then(() => {
          stop();
          setPlaying(true);
        })
        .catch(() => {
          el.muted = true;
        });
    };
    events.forEach((t) => document.addEventListener(t, unlock, true));

    return () => {
      stop();
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("ended", onEnd);
    };
  }, [testid]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused || el.muted) {
      el.muted = false;
      el.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const fmt = (s) => {
    if (!s || !isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  };

  return (
    <div className="border border-[#22222B] bg-[#08080A] p-5 tech-corner" data-testid={testid}>
      <audio ref={audioRef} src={src} preload="auto" loop />
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? "Pause" : "Play"}
          data-testid={`${testid}-toggle`}
          className="relative flex h-12 w-12 shrink-0 items-center justify-center border border-[#00F0FF]/50 hover:border-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors cursor-pointer"
        >
          {playing ? (
            <Pause className="h-5 w-5 text-[#CCFF00]" fill="currentColor" />
          ) : (
            <Play className="h-5 w-5 text-[#00F0FF]" fill="currentColor" />
          )}
          {playing && <span className="absolute inset-0 border border-[#CCFF00]/40 animate-ping pointer-events-none" />}
        </button>
        <div className="flex-1 min-w-0">
          <p className="mono text-[9px] tracking-[0.3em] text-[#00F0FF] mb-1">
            {playing ? "NOW PLAYING // " : ""}{kicker}
          </p>
          <p className="font-display uppercase text-base text-[#F2F2F5] truncate">{title}</p>
        </div>
        <span className="mono text-[10px] text-[#60606E] shrink-0" data-testid={`${testid}-time`}>
          {fmt(progress * duration)} / {fmt(duration)}
        </span>
      </div>
      <div className="mt-4 flex items-end gap-[2px] h-8" data-testid={`${testid}-bars`}>
        {Array.from({ length: BAR_COUNT }).map((_, i) => {
          const filled = i / BAR_COUNT <= progress;
          return (
            <span
              key={i}
              className={`flex-1 transition-colors duration-150 ${filled ? "bg-[#00F0FF]" : "bg-[#22222B]"} ${playing ? "eq-bar" : ""}`}
              style={{
                height: `${25 + ((i * 37) % 70)}%`,
                animationDelay: `${i * 55}ms`,
                animationDuration: "0.9s",
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
